import type { EmployeeDesignation, ExecutiveBriefingProfile } from '../types';
import { hasOfficeOpsPrivileges } from './employeeDesignations';

export interface BillingPayablesAccessArgs {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
  /** From `users/{uid}.executiveBriefing.profile` when that briefing is enabled. */
  briefingProfile?: ExecutiveBriefingProfile | null;
}

function isCoo(args: BillingPayablesAccessArgs): boolean {
  return !!args.designations?.includes('coo') || args.briefingProfile === 'coo';
}

const BILLING_HISTORY_EMPLOYEE_ROLES = new Set([
  'employee',
  'employee_office',
  'employee_shop',
  'staff',
]);

/** Billing & Payables hub — admin, EA, Office Manager, CFO, or COO. */
export function canManageBillingPayables(args: BillingPayablesAccessArgs): boolean {
  if (args.authRole === 'admin') return true;
  if (isCoo(args)) return true;
  return hasOfficeOpsPrivileges(args.designations) || !!args.designations?.includes('cfo');
}

/**
 * May approve individual payment installments for release. Deliberately
 * narrower than {@link canManageBillingPayables}: the executive assistant
 * who marks payments paid cannot self-approve them.
 */
export function canApproveInstallmentPayments(args: BillingPayablesAccessArgs): boolean {
  if (args.authRole === 'admin') return true;
  if (isCoo(args)) return true;
  return !!args.designations?.includes('cfo');
}

/**
 * Open Billing & Payables. Managers and project managers see the working queue.
 * Office, shop, and field employees can open it to read history, which stays collapsed.
 */
export function canViewBillingPayables(args: BillingPayablesAccessArgs): boolean {
  if (canManageBillingPayables(args) || canMarkReadyToBill(args)) return true;
  const role = String(args.authRole || '').trim().toLowerCase();
  return BILLING_HISTORY_EMPLOYEE_ROLES.has(role);
}

/** May mark items Ready to Bill (PMs can flag from Project Hub, managers from the hub page). */
export function canMarkReadyToBill(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
}): boolean {
  if (canManageBillingPayables(args)) return true;
  return !!args.designations?.includes('project_manager');
}
